import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, fmtTime, fileUrl, colorForId } from "../lib/api";
import { useTheme } from "../context/ThemeContext";
import { useAuth } from "../context/AuthContext";
import { useWebSocket } from "../lib/useWebSocket";
import { blip, tapSound } from "../lib/feedback";
import { Avatar, SignalBars, Modal, Field } from "../components/common";
import { MessageBubble, FeedItem, AdItem } from "../components/MessageBubble";
import { HudStatusBar, HudTicker, NetworkMonitor, buildTicker } from "../components/PlayerHUD";
import { ArrowLeft, Send, Paperclip, MessageSquare, Radio, Lock, Users, Megaphone, X, LogOut, Bell, Hash, CheckSquare, Square, Archive, ArchiveRestore, Inbox } from "lucide-react";
import { enablePush, pushSupported, pushPermission } from "../lib/push";
import { motion } from "framer-motion";

function NotifButton({ active, onClick }) {
  if (!pushSupported()) return null;
  return (
    <button data-testid="enable-notifications" onClick={onClick}
      className="text-[color:var(--r-text-muted)] hover:text-[color:var(--r-primary)]" style={{ color: active ? "var(--r-primary)" : undefined }}
      title={active ? "Notifications activées · appuyez pour tester" : "Activer les notifications"}>
      <Bell size={16} />
    </button>
  );
}

function useIsDesktop() {
  const [d, setD] = useState(typeof window !== "undefined" && window.innerWidth >= 1024);
  useEffect(() => {
    const on = () => setD(window.innerWidth >= 1024);
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, []);
  return d;
}

function DialerBar({ cid, onOpened }) {
  const [open, setOpen] = useState(false);
  const [rid, setRid] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  async function dial() {
    if (!rid.trim()) return;
    setErr(""); setBusy(true);
    try {
      const r = await api.post(`/campaigns/${cid}/dial`, { repla_id: rid.trim().toUpperCase() });
      setOpen(false); setRid("");
      onOpened(r.data.channel);
    } catch (e) { setErr(e?.response?.data?.detail || "Erreur réseau"); }
    finally { setBusy(false); }
  }
  return (
    <>
      <button data-testid="open-dialer" onClick={() => { setOpen(true); setErr(""); setRid(""); }} className="w-full flex items-center justify-center gap-2 py-3 border-b border-[color:var(--r-primary)]/10 tap font-mono text-xs tracking-[0.2em]" style={{ color: "var(--r-primary)" }}>
        <Hash size={14} /> COMPOSER UN ID
      </button>
      <Modal open={open} onClose={() => setOpen(false)} title="COMPOSER UN ID REPLA" width={400}>
        <Field label="IDENTIFIANT DU CONTACT">
          <input data-testid="dialer-input" className="input font-mono text-lg tracking-[0.25em] text-center" value={rid} onChange={(e) => setRid(e.target.value.toUpperCase())} placeholder="ABC-1234" autoFocus onKeyDown={(e) => { if (e.key === "Enter") dial(); }} />
        </Field>
        {err && <div data-testid="dialer-error" className="font-mono text-xs mb-3 py-2 px-3" style={{ color: "var(--r-alert)", border: "1px solid var(--r-alert)", background: "rgba(239,68,68,0.08)" }}>✕ {err}</div>}
        <button data-testid="dialer-call" disabled={busy || !rid.trim()} className={`btn btn-solid w-full tap flex items-center justify-center gap-2 ${busy ? "scanbar" : ""}`} onClick={dial}>
          <Send size={14} /> {busy ? "RECHERCHE DU NODE…" : "CONTACTER"}
        </button>
      </Modal>
    </>
  );
}


function ChannelItems({ list, onOpen, selectedId, selectMode = false, sel, onToggle }) {
  if (list.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 px-6 text-center" data-testid="empty-transmissions">
        <div className="radar"><span /><span /><span /><Radio size={22} style={{ color: "var(--r-primary)", opacity: 0.85 }} /></div>
        <div className="sys-label mt-6 leading-relaxed">AUCUNE FRÉQUENCE ACTIVE<br />EN ATTENTE DE SIGNAL</div>
      </div>
    );
  }
  return (
    <motion.div initial="hidden" animate="visible" variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.04 } } }}>
      {list.map((c) => {
        const npc = (c.participants_view || []).find((p) => p.type === "npc");
        const Icon = c.type === "private" ? Lock : c.type === "group" ? Users : Megaphone;
        const active = c.id === selectedId;
        const unread = c.unread > 0;
        const checked = selectMode && sel?.has(c.id);
        return (
          <motion.button
            key={c.id}
            variants={{ hidden: { opacity: 0, x: -10 }, visible: { opacity: 1, x: 0 } }}
            data-testid={`conversation-item-${c.id}`}
            onClick={() => (selectMode ? onToggle(c.id) : onOpen(c))}
            className="w-full text-left px-4 py-3.5 flex items-center gap-3.5 border-b border-[color:var(--r-primary)]/8 tap transition-colors relative hover:bg-[color:var(--r-primary)]/5"
            style={{ background: checked ? "color-mix(in srgb, var(--r-primary) 10%, transparent)" : (active ? "color-mix(in srgb, var(--r-primary) 13%, transparent)" : undefined), borderLeft: active && !selectMode ? "3px solid var(--r-primary)" : "3px solid transparent" }}
          >
            {selectMode && (
              <span data-testid={`conv-check-${c.id}`} className="shrink-0" style={{ color: checked ? "var(--r-primary)" : "var(--r-text-muted)" }}>
                {checked ? <CheckSquare size={18} /> : <Square size={18} />}
              </span>
            )}
            <div className="relative shrink-0">
              <Avatar src={npc?.avatar} name={c.name} size={46} square={c.type !== "private"} />
              {npc && c.type === "private" && (
                <span data-testid={`peer-status-${c.id}`} title={c.peer_online ? "En ligne" : "Hors ligne"}
                  className={`absolute -bottom-0.5 -right-0.5 rounded-full ${c.peer_online ? "presence-live" : ""}`}
                  style={{ width: 13, height: 13, border: "2px solid var(--r-panel)", background: c.peer_online ? "#22C55E" : "var(--r-text-muted)" }} />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <Icon size={12} className="shrink-0 text-[color:var(--r-text-muted)]" />
                <span className={`break-words leading-tight ${unread ? "font-bold" : "font-semibold"}`}>{c.name}</span>
                {c.type === "ad" && <span className="font-mono text-[8px] tracking-[0.15em] px-1 py-0.5 shrink-0" style={{ border: "1px solid color-mix(in srgb, var(--r-secondary) 50%, transparent)", color: "var(--r-secondary)" }}>PUB</span>}
              </div>
              <div className="text-xs truncate mt-0.5" style={{ color: unread ? "var(--r-text)" : "var(--r-text-muted)" }}>
                {c.last_message ? `${c.last_message.sender_name}: ${c.last_message.content}` : "—"}
              </div>
            </div>
            <div className="flex flex-col items-end gap-1.5 shrink-0">
              <span className="ts text-[color:var(--r-text-muted)]">{c.last_message ? fmtTime(c.last_message.created_at) : ""}</span>
              {unread && <span className="badge-pop font-mono text-[10px] font-bold px-1.5 py-0.5 rounded-full glow-primary" style={{ background: "var(--r-primary)", color: "#04131a" }}>{c.unread}</span>}
            </div>
          </motion.button>
        );
      })}
    </motion.div>
  );
}

function PlayerCommsTools({ archived, onToggleArchived, selectMode, onToggleSelect }) {
  return (
    <div className="flex items-center gap-2 px-3 py-2 border-b border-[color:var(--r-primary)]/10">
      <button data-testid="player-archived-toggle" onClick={onToggleArchived} className="btn btn-ghost text-[10px] flex items-center gap-1 flex-1 justify-center" style={{ borderColor: archived ? "var(--r-primary)" : undefined, color: archived ? "var(--r-primary)" : undefined }}>
        {archived ? <Inbox size={11} /> : <Archive size={11} />} {archived ? "ACTIVES" : "ARCHIVÉES"}
      </button>
      <button data-testid="player-select-toggle" onClick={onToggleSelect} className="btn btn-ghost text-[10px] flex items-center gap-1 flex-1 justify-center" style={{ borderColor: selectMode ? "var(--r-primary)" : undefined, color: selectMode ? "var(--r-primary)" : undefined }}>
        <CheckSquare size={11} /> {selectMode ? "ANNULER" : "SÉLECTIONNER"}
      </button>
    </div>
  );
}

function PlayerBulkBar({ count, archived, onApply, onCancel }) {
  return (
    <div className="border-t border-[color:var(--r-primary)]/15 panel p-2.5 flex items-center gap-2" data-testid="player-bulk-bar">
      <span className="sys-label whitespace-nowrap">{count} SÉL.</span>
      <button data-testid="player-bulk-apply" disabled={!count} onClick={onApply} className="btn btn-solid text-[10px] flex items-center gap-1 flex-1 justify-center disabled:opacity-40">
        {archived ? <><ArchiveRestore size={12} /> DÉSARCHIVER</> : <><Archive size={12} /> ARCHIVER</>}
      </button>
      <button data-testid="player-bulk-cancel" onClick={onCancel} className="btn btn-ghost text-[10px] px-2"><X size={12} /></button>
    </div>
  );
}

function Tabs({ tab, setTab }) {
  return (
    <div className="flex">
      <button data-testid="player-comms-tab" onClick={() => setTab("comms")} className="flex-1 flex items-center justify-center gap-2 py-3 border-b-2 tap transition-colors" style={{ color: tab === "comms" ? "var(--r-primary)" : "var(--r-text-muted)", borderColor: tab === "comms" ? "var(--r-primary)" : "transparent" }}>
        <MessageSquare size={16} /><span className="sys-label" style={{ color: "inherit" }}>TRANSMISSIONS</span>
      </button>
      <button data-testid="player-flux-tab" onClick={() => setTab("flux")} className="flex-1 flex items-center justify-center gap-2 py-3 border-b-2 tap transition-colors" style={{ color: tab === "flux" ? "var(--r-primary)" : "var(--r-text-muted)", borderColor: tab === "flux" ? "var(--r-primary)" : "transparent" }}>
        <Radio size={16} /><span className="sys-label" style={{ color: "inherit" }}>FLUX</span>
      </button>
    </div>
  );
}

export default function PlayerApp() {
  const { cid } = useParams();
  const nav = useNavigate();
  const { setTheme, networkName, logo, hud } = useTheme();
  const { logout, user } = useAuth();
  const desktop = useIsDesktop();

  const [channels, setChannels] = useState([]);
  const [tab, setTab] = useState("comms");
  const [openCh, setOpenCh] = useState(null);
  const [detail, setDetail] = useState(null);
  const [messages, setMessages] = useState([]);
  const [typing, setTyping] = useState(null);
  const typingTO = useRef(null);
  const [selectMode, setSelectMode] = useState(false);
  const [sel, setSel] = useState(() => new Set());
  const [showArchived, setShowArchived] = useState(false);

  const loadChannels = useCallback(async () => {
    const r = await api.get(`/campaigns/${cid}/channels`, { params: { archived: showArchived ? 1 : 0 } });
    setChannels(r.data);
  }, [cid, showArchived]);

  useEffect(() => { loadChannels(); }, [showArchived]); // eslint-disable-line

  function toggleSel(id) { setSel((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; }); }
  function exitSelect() { setSelectMode(false); setSel(new Set()); }
  async function playerArchive() {
    await api.post(`/campaigns/${cid}/channels/player-archive`, { channel_ids: [...sel], archived: !showArchived });
    exitSelect();
    loadChannels();
  }

  useEffect(() => {
    api.get("/me/campaigns").then((r) => {
      const c = r.data.find((x) => x.campaign_id === cid);
      if (c) setTheme(c.resolved);
    });
    loadChannels();
    const t = setInterval(() => {
      loadChannels();
      if (openRef.current) api.get(`/channels/${openRef.current.id}`).then((r) => setDetail(r.data)).catch(() => {});
    }, 15000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [cid]);

  const openChannel = useCallback(async (ch) => {
    setOpenCh(ch);
    setDetail(null);
    setMessages([]);
    const [d, m] = await Promise.all([
      api.get(`/channels/${ch.id}`),
      api.get(`/channels/${ch.id}/messages`),
    ]);
    setDetail(d.data);
    setMessages(m.data);
    api.post(`/channels/${ch.id}/read`).then(loadChannels).catch(() => {});
  }, [loadChannels]);

  const openRef = useRef(openCh); openRef.current = openCh;
  useWebSocket((ev) => {
    if (ev.event === "message") {
      const incoming = ev.message && ev.message.sender_type !== "player_character";
      if (incoming) blip();
      if (openRef.current && ev.channel_id === openRef.current.id) {
        setMessages((prev) => (prev.find((m) => m.id === ev.message.id) ? prev : [...prev, ev.message]));
        api.post(`/channels/${ev.channel_id}/read`).catch(() => {});
      }
      loadChannels();
    } else if (ev.event === "typing" && ev.from_gm) {
      if (openRef.current && ev.channel_id === openRef.current.id && ev.typing) {
        setTyping(ev.name);
        clearTimeout(typingTO.current);
        typingTO.current = setTimeout(() => setTyping(null), 5000);
      } else if (openRef.current && ev.channel_id === openRef.current.id) setTyping(null);
    } else if (ev.event === "message_deleted" || ev.event === "message_edited") {
      if (openRef.current && ev.channel_id === openRef.current.id)
        api.get(`/channels/${openRef.current.id}/messages`).then((r) => setMessages(r.data));
      loadChannels();
    } else if (ev.event === "channel_created" || ev.event === "channel_updated") {
      loadChannels();
    }
  });

  const comms = channels.filter((c) => c.type === "private" || c.type === "group");
  const feeds = channels.filter((c) => c.type === "info_descending" || c.type === "info_interactive" || c.type === "ad");
  const tickerItems = buildTicker(hud, feeds, networkName);
  const list = tab === "comms" ? comms : feeds;
  const isFeed = openCh && (openCh.type === "info_descending" || openCh.type === "info_interactive" || openCh.type === "ad");
  const isAd = openCh && openCh.type === "ad";

  function doLogout() { logout(); nav("/login"); }

  const [notifOn, setNotifOn] = useState(pushSupported() && pushPermission() === "granted");
  async function toggleNotif() {
    try {
      if (notifOn) {
        const r = await api.post("/push/test");
        alert(r.data.sent > 0 ? "Notification de test envoyée — vérifiez votre appareil." : "Aucun appareil abonné. Réactivez les notifications.");
      } else {
        await enablePush();
        setNotifOn(true);
      }
    } catch (e) { alert(e.message || "Notifications indisponibles"); }
  }

  // ---------------------------------------------------------------- DESKTOP
  if (desktop) {
    return (
      <div className="h-screen w-screen flex flex-col overflow-hidden" style={{ background: "var(--r-void)" }}>
        {hud?.enabled !== false && (<><HudStatusBar hud={hud} /><HudTicker items={tickerItems} /></>)}
        <div className="flex-1 flex min-h-0">
        <aside className="w-[380px] shrink-0 border-r border-[color:var(--r-primary)]/15 panel flex flex-col">
          <header className="h-14 px-4 flex items-center justify-between border-b border-[color:var(--r-primary)]/15">
            <div className="flex items-center gap-2 min-w-0">
              {logo && <img src={fileUrl(logo)} alt="" style={{ height: 22 }} />}
              <span className="font-title font-black tracking-tight text-glow truncate">{networkName}</span>
            </div>
            <div className="flex items-center gap-3">
              {user?.login_id && <span data-testid="my-repla-id" className="font-mono text-[10px] px-1.5 py-0.5 hidden sm:inline" title="Mon ID RÉPLA" style={{ color: "var(--r-primary)", border: "1px solid color-mix(in srgb, var(--r-primary) 30%, transparent)" }}>{user.login_id}</span>}
              <NotifButton active={notifOn} onClick={toggleNotif} />
              <button data-testid="logout-button" onClick={doLogout} className="text-[color:var(--r-text-muted)] hover:text-[color:var(--r-alert)]" title="Déconnexion"><LogOut size={16} /></button>
            </div>
          </header>
          <Tabs tab={tab} setTab={setTab} />
          <div className="flex-1 overflow-y-auto">
            {tab === "comms" && !selectMode && <DialerBar cid={cid} onOpened={openChannel} />}
            {tab === "comms" && <PlayerCommsTools archived={showArchived} onToggleArchived={() => { setShowArchived((a) => !a); exitSelect(); }} selectMode={selectMode} onToggleSelect={() => (selectMode ? exitSelect() : setSelectMode(true))} />}
            <ChannelItems list={list} onOpen={openChannel} selectedId={openCh?.id} selectMode={tab === "comms" && selectMode} sel={sel} onToggle={toggleSel} />
          </div>
          {tab === "comms" && selectMode && <PlayerBulkBar count={sel.size} archived={showArchived} onApply={playerArchive} onCancel={exitSelect} />}
        </aside>
        <main className="flex-1 min-w-0">
          {openCh ? (
            <ChatView
              key={openCh.id}
              channel={detail || openCh}
              messages={messages}
              typing={typing}
              isFeed={isFeed}
              embedded
              onSent={(m) => setMessages((prev) => (prev.find((x) => x.id === m.id) ? prev : [...prev, m]))}
            />
          ) : (
            <NetworkMonitor hud={hud} networkName={networkName} />
          )}
        </main>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------- MOBILE / TABLET
  if (openCh) {
    return (
      <ChatView
        channel={detail || openCh}
        messages={messages}
        typing={typing}
        isFeed={isFeed}
        onBack={() => { setOpenCh(null); setDetail(null); setMessages([]); loadChannels(); }}
        onSent={(m) => setMessages((prev) => (prev.find((x) => x.id === m.id) ? prev : [...prev, m]))}
      />
    );
  }

  return (
    <div className="min-h-screen max-w-md mx-auto border-x border-[color:var(--r-primary)]/15 flex flex-col pb-16" style={{ background: "var(--r-void)" }}>
      <header className="h-14 px-4 flex items-center justify-between border-b border-[color:var(--r-primary)]/15 panel sticky top-0 z-30">
        <div className="flex items-center gap-2">
          {logo && <img src={fileUrl(logo)} alt="" style={{ height: 22 }} />}
          <span className="font-title font-black tracking-tight text-glow glitch-hover">{networkName}</span>
        </div>
        <div className="flex items-center gap-3">
          {user?.login_id && <span data-testid="my-repla-id-mobile" className="font-mono text-[10px] px-1.5 py-0.5" title="Mon ID RÉPLA" style={{ color: "var(--r-primary)", border: "1px solid color-mix(in srgb, var(--r-primary) 30%, transparent)" }}>{user.login_id}</span>}
          <span className="sys-label flex items-center gap-1 blink"><span style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--r-primary)" }} /> EN LIGNE</span>
          <NotifButton active={notifOn} onClick={toggleNotif} />
          <button data-testid="logout-button" onClick={doLogout} className="text-[color:var(--r-text-muted)] hover:text-[color:var(--r-alert)]"><LogOut size={16} /></button>
        </div>
      </header>

      {hud?.enabled !== false && (<><HudStatusBar hud={hud} /><HudTicker items={tickerItems} /></>)}

      <div className="flex-1">
        <div className="sys-label px-4 pt-4 pb-2">{tab === "comms" ? "TRANSMISSIONS" : "FLUX"}</div>
        {tab === "comms" && !selectMode && <DialerBar cid={cid} onOpened={openChannel} />}
        {tab === "comms" && <PlayerCommsTools archived={showArchived} onToggleArchived={() => { setShowArchived((a) => !a); exitSelect(); }} selectMode={selectMode} onToggleSelect={() => (selectMode ? exitSelect() : setSelectMode(true))} />}
        <ChannelItems list={list} onOpen={openChannel} selectedId={openCh?.id} selectMode={tab === "comms" && selectMode} sel={sel} onToggle={toggleSel} />
        {tab === "comms" && selectMode && <PlayerBulkBar count={sel.size} archived={showArchived} onApply={playerArchive} onCancel={exitSelect} />}
      </div>

      <nav className="fixed bottom-0 max-w-md w-full panel border-t border-[color:var(--r-primary)]/15 h-16 flex items-center justify-around z-30">
        <button data-testid="player-comms-tab" onClick={() => setTab("comms")} className="flex flex-col items-center gap-1" style={{ color: tab === "comms" ? "var(--r-primary)" : "var(--r-text-muted)" }}>
          <MessageSquare size={18} /><span className="sys-label" style={{ color: "inherit" }}>TRANSMISSIONS</span>
        </button>
        <button data-testid="player-flux-tab" onClick={() => setTab("flux")} className="flex flex-col items-center gap-1" style={{ color: tab === "flux" ? "var(--r-primary)" : "var(--r-text-muted)" }}>
          <Radio size={18} /><span className="sys-label" style={{ color: "inherit" }}>FLUX</span>
        </button>
      </nav>
    </div>
  );
}

function ChatView({ channel, messages, typing, isFeed, onBack, onSent, embedded }) {
  const [text, setText] = useState("");
  const [file, setFile] = useState(null);
  const [sending, setSending] = useState(false);
  const streamRef = useRef(null);
  const fileRef = useRef(null);
  const canWrite = channel.can_write;

  useEffect(() => { if (streamRef.current) streamRef.current.scrollTop = streamRef.current.scrollHeight; }, [messages, typing]);

  const ttRef = useRef(null);
  function onType() {
    api.post(`/channels/${channel.id}/typing`, { typing: true }).catch(() => {});
    clearTimeout(ttRef.current);
    ttRef.current = setTimeout(() => api.post(`/channels/${channel.id}/typing`, { typing: false }).catch(() => {}), 3000);
  }

  async function send() {
    if ((!text.trim() && !file) || sending) return;
    setSending(true);
    try {
      let attachment_id = null, message_type = "text";
      if (file) {
        const fd = new FormData(); fd.append("file", file);
        const up = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        attachment_id = up.data.id;
        message_type = (up.data.content_type || "").startsWith("image/") ? "image" : "file";
      }
      const r = await api.post(`/channels/${channel.id}/messages`, { content: text.trim(), message_type, attachment_id });
      setText(""); setFile(null);
      tapSound();
      onSent?.(r.data);
    } finally { setSending(false); }
  }

  const npc = (channel.participants_view || []).find((p) => p.type === "npc");
  const isAd = channel.type === "ad";
  const pjParts = (channel.participants_view || []).filter((p) => p.type === "player_character");
  const colorPj = channel.type === "private" ? (npc ? pjParts[0] : (pjParts.find((p) => p.name === channel.name) || pjParts[0])) : null;
  const pjColor = colorPj ? colorForId(colorPj.id) : null;
  const containerCls = embedded
    ? "h-full flex flex-col view-in"
    : "min-h-screen max-w-md mx-auto border-x border-[color:var(--r-primary)]/15 flex flex-col view-in";

  return (
    <div className={containerCls} style={{ background: "var(--r-void)" }}>
      <header className="h-14 px-3 flex items-center gap-3 border-b border-[color:var(--r-primary)]/15 panel sticky top-0 z-30" style={pjColor ? { borderLeft: `3px solid ${pjColor}` } : undefined}>
        {!embedded && <button data-testid="chat-back" onClick={onBack} className="text-[color:var(--r-text-muted)]"><ArrowLeft size={18} /></button>}
        <div className="relative" style={pjColor ? { borderRadius: "9999px", boxShadow: `0 0 0 2px ${pjColor}`, padding: 0 } : undefined} data-testid="chat-header-pj-ring">
          <Avatar src={npc?.avatar} name={channel.name} size={36} square={channel.type !== "private"} />
          {npc && channel.type === "private" && (
            <span className="absolute -bottom-0.5 -right-0.5 rounded-full" style={{ width: 11, height: 11, border: "2px solid var(--r-panel)", background: channel.peer_online ? "#22C55E" : "var(--r-text-muted)", boxShadow: channel.peer_online ? "0 0 6px #22C55E" : "none" }} />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold truncate" style={(pjColor && !npc) ? { color: pjColor } : undefined}>{channel.name}</div>
          <div className="sys-label" style={{ color: (!isFeed && npc && channel.type === "private" && channel.peer_online) ? "#22C55E" : undefined }}>
            {isFeed ? (isAd ? "● PUBLICITÉ" : "● DIFFUSION") : (npc && channel.type === "private" ? (channel.peer_online ? "● EN LIGNE" : "○ HORS LIGNE") : "IDENTITÉ VÉRIFIÉE")}
          </div>
        </div>
        <SignalBars />
      </header>

      <div ref={streamRef} className="flex-1 overflow-y-auto comms-bg">
        <div className="max-w-3xl w-full mx-auto px-3 sm:px-6 py-4 flex flex-col">
          {messages.map((m, i) => {
            if (isAd) return <AdItem key={m.id} msg={m} />;
            if (isFeed) return <FeedItem key={m.id} msg={m} />;
            const prev = messages[i - 1];
            const grouped = !!prev && prev.sender_name === m.sender_name && (!!prev.own === !!m.own) && (new Date(m.created_at) - new Date(prev.created_at) < 180000);
            return <MessageBubble key={m.id} msg={m} grouped={grouped} decrypt={i === 0} showSender={channel.type === "group"} />;
          })}
        </div>
        {typing && (
          <div className="max-w-3xl w-full mx-auto px-3 sm:px-6 pb-3">
            <div className="pill-up inline-flex items-center gap-2 px-3 py-1.5 rounded-full" style={{ background: "color-mix(in srgb, var(--r-panel) 90%, transparent)", border: "1px solid color-mix(in srgb, var(--r-primary) 25%, transparent)" }}>
              <span className="text-xs" style={{ color: "var(--r-text-muted)" }}>{typing} transmet une donnée</span>
              <span><span className="tdot" /><span className="tdot" /><span className="tdot" /></span>
            </div>
          </div>
        )}
      </div>

      {canWrite ? (
        <div className="panel border-t border-[color:var(--r-primary)]/15 p-2 sm:p-3 backdrop-blur-md" style={embedded ? {} : { position: "sticky", bottom: 0 }}>
          <div className="max-w-3xl w-full mx-auto">
            {file && <div className="flex items-center gap-2 text-xs font-mono px-1 pb-2"><Paperclip size={12} /> {file.name} <button onClick={() => setFile(null)}><X size={12} /></button></div>}
            <div className="composer-box flex items-end gap-1.5 rounded-xl p-1.5" style={{ background: "var(--r-void)", border: "1px solid color-mix(in srgb, var(--r-primary) 25%, transparent)" }}>
              <input ref={fileRef} type="file" className="hidden" onChange={(e) => setFile(e.target.files[0] || null)} />
              <button data-testid="attach-button" onClick={() => fileRef.current?.click()} className="p-2.5 rounded-lg tap transition-colors hover:bg-[color:var(--r-primary)]/10" style={{ color: "var(--r-text-muted)" }}><Paperclip size={16} /></button>
              <textarea
                data-testid="chat-message-input"
                rows={1}
                className="flex-1 bg-transparent px-2 py-2.5 text-sm resize-none outline-none"
                style={{ color: "var(--r-text)", maxHeight: 128 }}
                placeholder="Message…"
                value={text}
                onChange={(e) => { setText(e.target.value); onType(); }}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              />
              <button data-testid="chat-send-button" onClick={send} disabled={sending || (!text.trim() && !file)} className="btn btn-solid px-4 py-2.5 rounded-lg tap flex items-center gap-1.5 disabled:opacity-40"><Send size={16} /></button>
            </div>
          </div>
        </div>
      ) : (
        <div className="panel border-t border-[color:var(--r-primary)]/15 p-3 text-center sys-label" style={embedded ? {} : { position: "sticky", bottom: 0 }}>CANAL EN LECTURE SEULE</div>
      )}
    </div>
  );
}
